import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";

// Define the base store configuration
// Note: This store uses dedicated selected_traits endpoint for optimal performance
const selectedTraitsBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/selected_traits", // Used for dedicated API calls
  name: "SelectedTraitsStoreRefactor",
  objectName: "SelectedTrait",
  enableCache: true, // Enable 5-minute cache
  actionCableChannel: null, // Real-time updates could be added later
  infiniteLoading: false, // Simple data accumulation without pagination
});

export const useSelectedTraitsStoreRefactor = defineStore(
  "selectedTraitsRefactor",
  {
    ...selectedTraitsBaseStore,

    state: () => ({
      ...selectedTraitsBaseStore.state(),
    }),

    getters: {
      ...selectedTraitsBaseStore.getters,

      /**
       * Get all selected traits for a specific account
       * @param {number} accountId - The account ID
       * @returns {Array} All selected traits for the account (including inactive)
       */
      getTraitsByAccount: (state) => (accountId) => {
        if (!accountId) return [];
        return state.data.filter(
          (trait) => trait && trait.account_id === accountId,
        );
      },

      /**
       * Get active selected traits for a specific account (for account detail display)
       * @param {number} accountId - The account ID
       * @returns {Array} Active selected traits with trait_value = 1
       */
      getActiveTraitsByAccount: (state) => (accountId) => {
        if (!accountId) return [];
        // New semantics: all returned associations are considered active
        return state.data.filter(
          (trait) => trait && trait.account_id === accountId,
        );
      },

      /**
       * Get selected traits for a specific report (for report item display)
       * @param {number} reportId - The report ID
       * @returns {Array} Selected traits associated with the report
       */
      getTraitsByReport: (state) => (reportId) => {
        if (!reportId) return [];
        return state.data.filter(
          (trait) => trait && trait.report_id === reportId,
        );
      },

      /**
       * Get selected trait IDs for a specific account (for report form pre-population)
       * @param {number} accountId - The account ID
       * @returns {Array<number>} Array of trait IDs that are currently selected
       */
      getSelectedTraitIds: (state) => (accountId) => {
        if (!accountId) return [];
        return state.data
          .filter((trait) => trait && trait.account_id === accountId)
          .map((trait) => trait.trait_id);
      },

      // Backward compatibility getter
      traits: (state) => state.data,
    },

    actions: {
      ...selectedTraitsBaseStore.actions,

      /**
       * Fetch selected traits for a specific account using dedicated API endpoint
       * Always fetches fresh data to ensure up-to-date information
       * @param {number} accountId - The account ID
       * @returns {Array} Selected traits for the account
       */
      async fetchTraitsByAccount(accountId) {
        this.loading = true;
        this.error = null;

        try {
          if (!accountId) {
            console.warn(
              "SelectedTraitsStoreRefactor: fetchTraitsByAccount called without accountId",
            );
            return [];
          }

          // Always fetch fresh data - no caching
          console.log(
            "SelectedTraitsStoreRefactor: Fetching fresh traits for account ID:",
            accountId,
          );

          // Call the dedicated API endpoint
          const response = await this.customApiCall(
            "GET",
            `/api/v1/accounts/${accountId}/selected_traits`,
            {},
            null, // No success message needed
          );

          // Handle the API response format
          const selectedTraits = response?.data || [];

          console.log(
            "SelectedTraitsStoreRefactor: API response received:",
            response,
          );

          // Clear existing traits for this account first, then add new ones
          this.data = this.data.filter(
            (trait) => trait.account_id !== accountId,
          );

          // Add the fresh traits
          selectedTraits.forEach((trait) => {
            if (trait && trait.id) {
              this._upsertItem(trait);
            }
          });

          console.log(
            `SelectedTraitsStoreRefactor: Refreshed ${selectedTraits.length} traits for account ${accountId}. Total traits: ${this.data.length}`,
          );

          return this.getTraitsByAccount(accountId);
        } catch (error) {
          this.error = error.message || "Error fetching traits by account";
          console.error(
            "SelectedTraitsStoreRefactor: Error fetching traits:",
            error,
          );
          return [];
        } finally {
          this.loading = false;
        }
      },

      /**
       * Clear traits for a specific account
       * @param {number} accountId - The account ID
       */
      clearAccountData(accountId) {
        this.data = this.data.filter((trait) => trait.account_id !== accountId);
      },

      /**
       * Clear all data
       */
      clearAllData() {
        this.data = [];
      },
    },
  },
);
